import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, Building2, X, Trash2, Star } from "lucide-react";

const EMPTY_FORM = { name: "", code: "", currency: "GBP", is_default: false };

export default function Entities() {
  const [entities, setEntities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/entities");
      setEntities(data || []);
    } catch {
      toast.error("Failed to load entities");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/entities", form);
      toast.success("Entity created");
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create entity");
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async (e) => {
    try {
      await api.put(`/entities/${e.entity_id}`, { is_default: true });
      toast.success(`${e.name} is now the default entity`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update entity");
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete entity "${e.name}"?`)) return;
    try {
      await api.delete(`/entities/${e.entity_id}`);
      toast.success("Entity deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete entity");
    }
  };

  return (
    <DarkPage><div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Business Entities</h1>
          <p className="text-sm text-[#7d8590] mt-1">
            Segment projects and reporting into separate legal entities or divisions within this workspace — e.g. "Acme UK Ltd" vs "Acme US Inc". Optional: without any entities, everything works exactly as before.
          </p>
        </div>
        <Button onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Entity
        </Button>
      </div>

      {entities.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">
          No entities yet. Create one to start segmenting projects and reports by legal entity or division.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {entities.map(e => (
            <div key={e.entity_id} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-1.5 text-white font-semibold">
                  <Building2 className="w-3.5 h-3.5 text-[#7d8590]" /> {e.name}
                  {e.is_default && <Star className="w-3.5 h-3.5 text-[#00F5D4] fill-[#00F5D4]" />}
                </div>
                <button onClick={() => remove(e)} className="p-1.5 text-[#7d8590] hover:text-rose-400" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="text-xs text-[#7d8590] mb-3">{e.code || "—"} · {e.currency}</div>
              {!e.is_default && (
                <Button onClick={() => setDefault(e)} className="w-full bg-[#161b22] border border-[#30363d] text-white hover:bg-[#1c2128] text-xs h-8">
                  Make default
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Entity</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Acme UK Ltd"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Code</label>
                  <input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                    placeholder="UK"
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    {["GBP", "USD", "EUR"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-[#7d8590]">
                <input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} />
                Make this the default entity
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={create} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Creating…" : "Create entity"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
